import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { getRelativeDuration } from 'common/helpers';
import { MACHINE_CONDITION } from 'common/constants';
import Popover from 'common/components/molecules/Popover';

const CRITICAL = '#d92b2b';
const WARNING = '#e8890c';
const INK = '#383838';
const INK_2 = '#6b6e66';
const INK_3 = '#999b95';
const LINE = '#e5e8e1';
const LINE_2 = '#eff0ee';
const BG = '#f6f7f4';
const GREEN = '#91b846';

const CONDITION_META = MACHINE_CONDITION.reduce((acc, opt) => {
  acc[opt.value] = opt;
  return acc;
}, {});

// reason type -> chip presentation. Severity colours only for severity reasons.
const REASON_ORDER = ['alarms', 'diagnostics', 'diagnostics_not_evaluable', 'health_score', 'baselines_status', 'missing_asset_info', 'tasks'];
// Non-analyst roles get a stripped "my work" view: only actionable work reasons
// (no analytical alarms/diagnostics/health-score, no urgency score).
export const ACTIONABLE_REASONS = ['missing_asset_info', 'tasks'];
const REASON_CONFIG = {
  alarms: { glyph: '⚠', titleKey: 'assets.triage.reasons.openAlarm', titleDefault: 'Open Alarm', tone: 'critical' },
  diagnostics: { glyph: '⬡', titleKey: 'assets.triage.reasons.diagnosticRule', titleDefault: 'Diagnostic', tone: 'warning' },
  diagnostics_not_evaluable: { glyph: '!', titleKey: 'assets.triage.reasons.diagnosticsNotEvaluable', titleDefault: "Diagnostics can't run", tone: 'muted' },
  health_score: { glyph: '♥', titleKey: 'assets.triage.reasons.healthScore', titleDefault: 'Health Score', tone: 'warning' },
  baselines_status: { glyph: '◳', titleKey: 'assets.triage.reasons.baselinesNotConfigured', titleDefault: 'Baselines', tone: 'muted' },
  missing_asset_info: { glyph: '◷', titleKey: 'assets.triage.reasons.missingAssetInfo', titleDefault: 'Missing info', tone: 'muted' },
  tasks: { glyph: '✓', titleKey: 'assets.triage.reasons.openTask', titleDefault: 'Open task', tone: 'muted' }
};
const TONE_COLORS = {
  critical: { fg: CRITICAL, bg: '#fcefef', border: '#f2c9c9' },
  warning: { fg: WARNING, bg: '#fdf4e7', border: '#f0d9b8' },
  muted: { fg: INK_2, bg: BG, border: LINE }
};

export const getUrgencyBucket = (score) => {
  const value = Number(score) || 0;
  if (value >= 70) return { key: 'urgent', labelKey: 'assets.triage.urgency.urgent', labelDefault: 'Urgent', color: CRITICAL };
  if (value >= 40) return { key: 'elevated', labelKey: 'assets.triage.urgency.elevated', labelDefault: 'Elevated', color: WARNING };
  return { key: 'routine', labelKey: 'assets.triage.urgency.routine', labelDefault: 'Routine', color: '#c9ccc4' };
};

const SCORE_TONES = {
  urgent: { bg: '#fcefef', fg: CRITICAL },
  elevated: { bg: '#fdf4e7', fg: WARNING },
  routine: { bg: LINE_2, fg: INK_2 }
};

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  background: white;
  border: 1px solid ${props => (props.selected ? GREEN : LINE)};
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: border-color 0.12s ease, box-shadow 0.12s ease, opacity 0.2s ease;
  ${props => props.selected && `box-shadow: 0 0 0 1px ${GREEN};`}
  ${props => props.viewed && `opacity: 0.55; background: ${BG};`}

  &:hover {
    border-color: ${props => (props.selected ? GREEN : INK_3)};
    box-shadow: ${props => (props.selected ? `0 0 0 1px ${GREEN}` : '0 1px 3px rgba(40,42,36,0.1)')};
  }
  &:hover .pickbox { opacity: 1; }
  &:focus-visible {
    outline: 2px solid ${GREEN};
    outline-offset: 2px;
  }
`;

const Score = styled.div`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 9px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-weight: 800;
  font-size: 13.5px;
  background: ${props => props.bg};
  color: ${props => props.fg};

  small {
    font-size: 7px;
    font-weight: 700;
    letter-spacing: 0.4px;
    margin-top: 2px;
    opacity: 0.75;
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const TopLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${props => props.color || '#c9ccc4'};
`;

const Name = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${INK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AreaText = styled.span`
  font-size: 11px;
  color: ${INK_3};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
  flex-wrap: wrap;
`;

const ReasonChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  border-radius: 5px;
  padding: 2px 7px;
  white-space: nowrap;
  color: ${props => props.fg};
  background: ${props => props.bg};
  border: 1px solid ${props => props.border};
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const SetupCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${INK_3};
  width: 86px;
`;

const SetupBar = styled.span`
  display: inline-block;
  width: 42px;
  height: 5px;
  border-radius: 3px;
  background: ${LINE_2};
  overflow: hidden;

  i {
    display: block;
    height: 100%;
    width: ${props => props.progress}%;
    background: #aeb3a8;
  }
`;

const Since = styled.span`
  font-size: 11px;
  color: ${INK_3};
  width: 56px;
  text-align: right;
`;

const ReviewCheck = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1.5px solid ${props => (props.reviewed ? GREEN : LINE)};
  background: ${props => (props.reviewed ? '#edf3e1' : 'white')};
  color: ${props => (props.reviewed ? '#53682c' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    border-color: ${props => (props.reviewed ? GREEN : INK_3)};
  }
`;

const PickBox = styled.span`
  width: 18px;
  height: 18px;
  min-width: 18px;
  border-radius: 5px;
  border: 1.5px solid ${props => (props.picked ? GREEN : INK_3)};
  background: ${props => (props.picked ? GREEN : 'white')};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  cursor: pointer;
  flex-shrink: 0;
  opacity: ${props => (props.forceShow ? 1 : 0)};
  transition: opacity 0.12s ease;
`;

const SnoozeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  color: ${INK_3};
  background: ${LINE_2};
  border-radius: 5px;
  padding: 1px 6px;
  white-space: nowrap;
`;

const Star = styled.button`
  border: none;
  background: transparent;
  padding: 2px;
  cursor: pointer;
  display: inline-flex;
  color: ${props => (props.on ? '#e8b04a' : '#c7cabf')};
  flex-shrink: 0;

  &:hover { color: ${props => (props.on ? '#e0a02e' : INK_3)}; }
  svg { width: 15px; height: 15px; }
`;

const Unsnooze = styled.button`
  font-size: 11px;
  font-weight: 700;
  color: #53682c;
  cursor: pointer;
  border: 1px solid ${LINE};
  border-radius: 6px;
  padding: 3px 9px;
  background: white;
  flex-shrink: 0;

  &:hover { border-color: ${GREEN}; background: #edf3e1; }
`;

const Ovf = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const OvfBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${INK_3};
  font-size: 16px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 5px;

  &:hover { background: ${LINE_2}; color: ${INK}; }
`;

const OvfMenu = styled.div`
  min-width: 168px;
  background: white;
  border: 1px solid ${LINE};
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(40, 44, 36, 0.16);
  padding: 4px;
`;

const OvfLabel = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${INK_3};
  padding: 6px 8px 3px;
`;

const OvfItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  font-family: inherit;
  color: ${INK};
  padding: 7px 8px;
  border-radius: 6px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  background: none;
  border: 0;

  &:hover { background: ${LINE_2}; }
`;

const OvfDivider = styled.div`
  height: 1px;
  background: ${LINE_2};
  margin: 4px 2px;
`;

const SNOOZE_DURATIONS = [
  { days: 1, labelKey: 'assets.triage.snooze.1d', def: '1 day' },
  { days: 3, labelKey: 'assets.triage.snooze.3d', def: '3 days' },
  { days: 7, labelKey: 'assets.triage.snooze.1w', def: '1 week' },
  { days: 30, labelKey: 'assets.triage.snooze.1m', def: '1 month' }
];

const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
  </svg>
);

const formatSnoozeUntil = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatTooltipDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

class AttentionCard extends React.Component {
  state = { ovfOpen: false };

  stop = (e) => { e.stopPropagation(); };

  isActivateKey = e => (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar');

  onRowKeyDown = (e) => {
    if (this.isActivateKey(e)) {
      e.preventDefault();
      this.props.onSelect(this.props.item.id);
    }
  };

  onPickKeyDown = (e) => {
    if (this.isActivateKey(e)) {
      e.preventDefault();
      this.stop(e);
      this.props.onTogglePick(this.props.item.id);
    }
  };

  setOvfBtnRef = (node) => { this.ovfBtnEl = node; };

  getOvfBtn = () => this.ovfBtnEl;

  toggleOvf = (e) => {
    this.stop(e);
    this.setState(prev => ({ ovfOpen: !prev.ovfOpen }));
  };

  closeOvf = () => {
    this.setState({ ovfOpen: false });
  };

  reasonKeys = () => {
    const { item, stripped } = this.props;
    const reason = item.reason_for_attention || {};
    const order = stripped ? ACTIONABLE_REASONS : REASON_ORDER;
    return order.filter(key => reason[key] && reason[key].length > 0);
  };

  toggleReviewed = (e) => {
    this.stop(e);
    const { item, onToggleReviewed } = this.props;
    onToggleReviewed(item.id, !!item.attention_machine_viewed);
  };

  onSnooze = (days) => {
    this.closeOvf();
    this.props.onSnooze(this.props.item.id, days);
  };

  onToggleWatch = (e) => {
    if (e) this.stop(e);
    this.closeOvf();
    const { item, onToggleWatch } = this.props;
    onToggleWatch(item.id, !item.is_watched);
  };

  renderOvfMenu = () => {
    const { item, canWatchlist, onOpenAsset, t } = this.props;
    return (
      <OvfMenu onClick={this.stop} role="menu">
        <OvfLabel>{t('assets.triage.snooze.label', { defaultValue: 'Snooze for…' })}</OvfLabel>
        {SNOOZE_DURATIONS.map(d => (
          <OvfItem key={d.days} type="button" role="menuitem" onClick={() => this.onSnooze(d.days)}>
            {`🕐 ${t(d.labelKey, { defaultValue: d.def })}`}
          </OvfItem>
        ))}
        <OvfDivider />
        {canWatchlist && (
          <OvfItem type="button" role="menuitem" onClick={this.onToggleWatch}>
            {item.is_watched
              ? `★ ${t('assets.triage.removeWatch', { defaultValue: 'Remove from watchlist' })}`
              : `☆ ${t('assets.triage.addWatch', { defaultValue: 'Add to watchlist' })}`}
          </OvfItem>
        )}
        <OvfItem type="button" role="menuitem" onClick={() => { this.closeOvf(); onOpenAsset(item.id); }}>
          {t('assets.triage.openAsset', { defaultValue: 'Open asset page' })}
        </OvfItem>
      </OvfMenu>
    );
  };

  render() {
    const {
      item, onSelect, selected, anySelected, picked, onTogglePick, onUnsnooze, canWatchlist, stripped, t
    } = this.props;
    const score = Number(item.urgency_score) || 0;
    const bucket = getUrgencyBucket(score);
    const scoreTone = SCORE_TONES[bucket.key];
    const condition = CONDITION_META[item.condition];
    const progress = Math.max(0, Math.min(100, Number(item.setup_completion_percent) || 0));
    const reviewed = !!item.attention_machine_viewed;
    const snoozed = !!item.is_snoozed;
    const keys = this.reasonKeys();
    const shownKeys = keys.slice(0, 3);
    const extra = keys.length - shownKeys.length;
    const setupTitle = t('assets.triage.setupCompletionTooltip', {
      defaultValue: 'Setup completion: {{progress}}%',
      progress
    });
    const conditionLabel = condition
      ? t(`machineCondition.${condition.value}`, { defaultValue: condition.text })
      : t('assets.triage.conditionUnknown', { defaultValue: 'Condition unknown' });
    const watchLabel = item.is_watched
      ? t('assets.triage.removeWatch', { defaultValue: 'Remove from watchlist' })
      : t('assets.triage.addWatch', { defaultValue: 'Add to watchlist' });
    const reviewLabel = reviewed
      ? t('assets.triage.markUnreviewed', { defaultValue: 'Mark unreviewed' })
      : t('assets.triage.markReviewed', { defaultValue: 'Mark Reviewed' });
    const conditionSinceTitle = item.condition_since
      ? t('assets.triage.conditionSinceTooltip', {
        defaultValue: 'Condition since: {{date}}',
        date: formatTooltipDate(item.condition_since)
      })
      : t('assets.triage.conditionSinceMissingTooltip', {
        defaultValue: 'Condition since: not available'
      });

    return (
      <Row
        selected={selected}
        viewed={reviewed || snoozed}
        onClick={() => onSelect(item.id)}
        onKeyDown={this.onRowKeyDown}
        role="button"
        tabIndex={0}
        aria-current={selected}
      >
        {!snoozed && (
          <PickBox
            className="pickbox"
            role="checkbox"
            tabIndex={0}
            aria-checked={picked}
            aria-label={t('assets.triage.selectAsset', { defaultValue: 'Select {{name}}', name: item.name })}
            picked={picked}
            forceShow={picked || anySelected}
            onClick={(e) => { this.stop(e); onTogglePick(item.id); }}
            onKeyDown={this.onPickKeyDown}
          >
            <span aria-hidden="true">{picked ? '✓' : ''}</span>
          </PickBox>
        )}
        {!stripped && (
          <Score bg={scoreTone.bg} fg={scoreTone.fg}>
            {score}
            <small>SCORE</small>
          </Score>
        )}
        <Body>
          <TopLine>
            <Dot
              color={condition ? condition.color : null}
              role="img"
              aria-label={conditionLabel}
              title={conditionLabel}
            />
            <Name>{item.name}</Name>
            <AreaText>{item.area_path || item.area_name || '--'}</AreaText>
            {snoozed && (
              <SnoozeBadge>{`🕐 ${t('assets.triage.until', { defaultValue: 'until' })} ${formatSnoozeUntil(item.snoozed_until)}`}</SnoozeBadge>
            )}
          </TopLine>
          <Chips>
            {shownKeys.map((key) => {
              const cfg = REASON_CONFIG[key];
              const tone = TONE_COLORS[cfg.tone];
              return (
                <ReasonChip key={key} fg={tone.fg} bg={tone.bg} border={tone.border}>
                  <span>{cfg.glyph}</span>
                  {t(cfg.titleKey, { defaultValue: cfg.titleDefault })}
                </ReasonChip>
              );
            })}
            {extra > 0 && (
              <ReasonChip fg={INK_3} bg={BG} border={LINE}>{`+${extra}`}</ReasonChip>
            )}
          </Chips>
        </Body>
        {snoozed ? (
          <Meta>
            <Since title={conditionSinceTitle}>{item.condition_since ? getRelativeDuration(item.condition_since) : '--'}</Since>
            <Unsnooze
              type="button"
              onClick={(e) => { this.stop(e); onUnsnooze(item.id); }}
            >
              {t('assets.triage.unsnooze', { defaultValue: 'Unsnooze' })}
            </Unsnooze>
          </Meta>
        ) : (
          <Meta>
            <SetupCell title={setupTitle}>
              <SetupBar progress={progress}><i /></SetupBar>
              {`${progress}%`}
            </SetupCell>
            <Since title={conditionSinceTitle}>{item.condition_since ? getRelativeDuration(item.condition_since) : '--'}</Since>
            {canWatchlist && (
              <Star
                type="button"
                on={!!item.is_watched}
                onClick={this.onToggleWatch}
                aria-pressed={!!item.is_watched}
                aria-label={watchLabel}
                title={watchLabel}
              >
                <StarIcon />
              </Star>
            )}
            <Ovf>
              <OvfBtn
                type="button"
                innerRef={this.setOvfBtnRef}
                onClick={this.toggleOvf}
                aria-haspopup="menu"
                aria-expanded={this.state.ovfOpen}
                aria-label={t('assets.triage.moreActions', { defaultValue: 'More actions' })}
                title={t('assets.triage.moreActions', { defaultValue: 'More actions' })}
              >
                ⋯
              </OvfBtn>
              <Popover open={this.state.ovfOpen} getAnchor={this.getOvfBtn} onClose={this.closeOvf} align="right">
                {this.renderOvfMenu()}
              </Popover>
            </Ovf>
            <ReviewCheck
              type="button"
              reviewed={reviewed}
              onClick={this.toggleReviewed}
              aria-pressed={reviewed}
              aria-label={reviewLabel}
              title={reviewLabel}
            >
              <span aria-hidden="true">✓</span>
            </ReviewCheck>
          </Meta>
        )}
      </Row>
    );
  }
}

AttentionCard.propTypes = {
  item: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  onToggleReviewed: PropTypes.func.isRequired,
  onSnooze: PropTypes.func,
  onUnsnooze: PropTypes.func,
  onToggleWatch: PropTypes.func,
  onOpenAsset: PropTypes.func,
  onTogglePick: PropTypes.func,
  anySelected: PropTypes.bool,
  picked: PropTypes.bool,
  canWatchlist: PropTypes.bool,
  stripped: PropTypes.bool,
  selected: PropTypes.bool,
  t: PropTypes.func.isRequired
};

AttentionCard.defaultProps = {
  selected: false,
  anySelected: false,
  picked: false,
  canWatchlist: false,
  stripped: false,
  onSnooze: () => {},
  onUnsnooze: () => {},
  onToggleWatch: () => {},
  onOpenAsset: () => {},
  onTogglePick: () => {}
};

export default withTranslation()(AttentionCard);
